/**
 * MobileMarkets — the FULL IG watch list (all 34 instruments), grouped by asset class.
 *
 * CLUSTER-1 (research-visibility uplift). This screen consumes the dedicated `markets_strip_v1()` RPC
 * (the single additive data contract shared by mobile/web/Android Auto — see build plan §1) and renders
 * every row, grouped into Indices / FX / Commodities / ETFs & Bonds, with price, day change %, 7d
 * change %, up/down colour and an open/closed market-state dot.
 *
 * `markets_strip_v1` fixes a bug in the previous `mobile_home_feed`-sourced `change_pct` field, which is
 * always null for IG rows: `day_change_pct` is `COALESCE(change_1d, change_pct)` and is live for all IG
 * rows today. `week_change_pct` is computed from `price_history` 7-calendar-days-ago close; it is NULL
 * until IG daily-close history accrues (a separate, out-of-scope backfill) and renders as "—" until then
 * — never a zero or a fabricated number.
 *
 * READ-ONLY and offline-aware, mirroring the sibling mobile pages exactly:
 *   • ONE useCachedQuery reads `markets_strip_v1` (queryFn THROWS on { error } so the cached
 *     last-known-good keeps serving offline rather than being overwritten with a partial/empty value).
 *   • A stable EMPTY envelope guarantees the screen is never blank (offline cold-start renders a
 *     coherent shell with quiet empty states).
 *   • The "Saved <relative time>" header hint + amber-when-offline colour mirror the sibling pages.
 *
 * A non-research user gets `[]` from the RPC — a valid, non-error state → a calm empty card, never an
 * error banner. Only a signed-out caller raises (28000), surfaced as a hard error.
 *
 * The generated supabase types are stale for this RPC, so the call is cast through `any` (matching the
 * rest of the mobile surfaces). No writes. Web/desktop are untouched (this is a /partner/m/* surface).
 */
import { formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';
import { ChevronLeft, TrendingDown, TrendingUp } from 'lucide-react';
import { useIsOnline } from '@/hooks/useIsOnline';
import { useCachedQuery } from '@/lib/offline/cachedQuery';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Shapes — the markets_strip_v1() RPC contract (build plan §1)
// ---------------------------------------------------------------------------

interface MarketStripRow {
  ticker: string;
  label: string | null;
  price: number | null;
  market_state: string | null;
  epic: string | null;
  day_change_pct: number | null;
  week_change_pct: number | null;
  as_of: string | null;
}

interface HomeFeed {
  market_strip: MarketStripRow[];
}

const EMPTY_FEED: HomeFeed = { market_strip: [] };

async function fetchHomeFeed(): Promise<HomeFeed> {
  const { data, error } = await (supabase.rpc as any)('markets_strip_v1');
  if (error) throw error;
  // The RPC returns the row array directly (not wrapped in { market_strip }) — shape-tolerant in case
  // a future revision wraps it.
  const rows = Array.isArray(data) ? data : Array.isArray((data as any)?.market_strip) ? (data as any).market_strip : [];
  return { market_strip: rows as MarketStripRow[] };
}

// ---------------------------------------------------------------------------
// Asset-class grouping — derived from the IG ticker code (no stored asset-class column).
//   • Indices       : IX.D.* and the FTSE Mid 250 (KB.D.MID250)
//   • FX            : the CS.D.<CCC><CCC>.TODAY currency-pair tickers
//   • Commodities   : CC.D.* (energy) + Spot Gold (CS.D.USCGC)
//   • ETFs & Bonds  : everything else (KA./SI./ED./UC. etc.)
// The order of the groups is the display order.
// ---------------------------------------------------------------------------

type AssetClass = 'Indices' | 'FX' | 'Commodities' | 'ETFs & Bonds';

const GROUP_ORDER: AssetClass[] = ['Indices', 'FX', 'Commodities', 'ETFs & Bonds'];

// The CS.D. currency pairs in the watch list. Spot Gold also uses the CS.D. prefix but is a commodity,
// so we match on an explicit FX-pair pattern rather than the prefix alone.
const FX_PAIR_RE = /^IG:CS\.D\.[A-Z]{6}\.TODAY/i;

function classify(row: MarketStripRow): AssetClass {
  const t = (row.ticker || '').toUpperCase();
  if (FX_PAIR_RE.test(t)) return 'FX';
  if (t.startsWith('IG:CC.D.')) return 'Commodities';
  if (t.includes('USCGC')) return 'Commodities'; // Spot Gold (CS.D.USCGC) is a commodity
  if (t.startsWith('IG:IX.D.')) return 'Indices';
  if (t.startsWith('IG:KB.D.MID250')) return 'Indices'; // FTSE Mid 250
  return 'ETFs & Bonds';
}

// ---------------------------------------------------------------------------
// Formatting helpers (match MobileHome)
// ---------------------------------------------------------------------------

function formatPct(value: number | null): string {
  if (value == null || Number.isNaN(value)) return '—';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
}

function formatPrice(value: number | null, isFx = false): string {
  if (value == null || Number.isNaN(value)) return '—';
  // FX pairs, and any small-magnitude quote (|price| < 10, e.g. sub-$10 ETFs / low FX crosses),
  // need 4dp to be legible; everything else stays at 2dp with thousands grouping.
  const dp = isFx || Math.abs(value) < 10 ? 4 : 2;
  return value.toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

// IG market_state strings vary (TRADEABLE / EDITS_ONLY / CLOSED / OFFLINE / AUCTION …). Treat anything
// that looks tradeable as "open"; null/unknown stays quiet (no dot label asserted).
function marketOpen(state: string | null): boolean | null {
  if (!state) return null;
  const s = state.toUpperCase();
  if (s === 'TRADEABLE' || s === 'OPEN') return true;
  return false;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function MobileMarkets() {
  const online = useIsOnline();

  // Share the home feed's cache scope key shape but a distinct cacheKey so this screen has its own
  // last-known-good envelope (it doesn't need the persona scoping — the strip is the same for all who
  // can see it; a non-research user gets []).
  const feed = useCachedQuery<HomeFeed>({
    queryKey: ['mobile-markets-feed'],
    cacheKey: 'mobile.markets_feed',
    queryFn: fetchHomeFeed,
  });

  const data = feed.data ?? EMPTY_FEED;
  const strip = data.market_strip;

  const showStale = feed.isFromCache && feed.cachedAt != null;
  let staleLabel: string | null = null;
  if (showStale && feed.cachedAt) {
    try {
      staleLabel = `Saved ${formatDistanceToNow(new Date(feed.cachedAt), { addSuffix: true })}`;
    } catch {
      staleLabel = 'Showing saved data';
    }
  }

  // Hard error only when there's nothing cached to fall back to (e.g. signed-out 28000 on a cold start).
  const hardError = feed.isError && !feed.isFromCache && feed.data === undefined;

  // Group + keep each group's incoming sort order (the RPC already sorts by label, ticker).
  const groups: Record<AssetClass, MarketStripRow[]> = {
    Indices: [],
    FX: [],
    Commodities: [],
    'ETFs & Bonds': [],
  };
  for (const row of strip) groups[classify(row)].push(row);

  const nonEmptyGroups = GROUP_ORDER.filter((g) => groups[g].length > 0);

  return (
    <div className="min-h-screen bg-background px-4 pb-8 pt-[calc(1.25rem+env(safe-area-inset-top))]">
      <header className="mb-4">
        <Link
          to="/partner/m/home"
          className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          Home
        </Link>
        <h1 className="font-heading text-2xl font-semibold text-primary">Live prices</h1>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
          {strip.length > 0 && (
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground/70">
              {strip.length} instrument{strip.length === 1 ? '' : 's'}
            </p>
          )}
          {showStale && staleLabel && (
            <p
              className={cn(
                'text-[11px] font-medium uppercase tracking-wide',
                online ? 'text-muted-foreground/70' : 'text-amber-600',
              )}
            >
              {staleLabel}
            </p>
          )}
        </div>
      </header>

      <div className="space-y-3">
        {hardError ? (
          <section className="rounded-xl border border-border bg-card p-4">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground/70">Couldn’t load</p>
            <p className="mt-2 text-sm text-muted-foreground">
              {online
                ? 'We couldn’t load the watch list just now. Pull back in a moment.'
                : 'You’re offline and there’s no saved market data yet. This will fill in once you reconnect.'}
            </p>
          </section>
        ) : strip.length === 0 ? (
          <section className="rounded-xl border border-border bg-card p-4">
            <h2 className="font-heading text-sm font-semibold">No market data</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Live market data isn’t available for your access level.
            </p>
            {!online && (
              <p className="mt-2 text-[11px] uppercase tracking-wide text-amber-600">
                You’re offline — this will refresh once you reconnect.
              </p>
            )}
          </section>
        ) : (
          nonEmptyGroups.map((group) => (
            <section key={group} className="rounded-xl border border-border bg-card p-4">
              <h2 className="font-heading text-sm font-semibold">{group}</h2>
              <ul className="mt-2 divide-y divide-border/60">
                {groups[group].map((m) => {
                  const dayUp = (m.day_change_pct ?? 0) >= 0;
                  const weekUp = (m.week_change_pct ?? 0) >= 0;
                  const open = marketOpen(m.market_state);
                  return (
                    <li key={m.ticker} className="flex items-center justify-between gap-3 py-2.5">
                      <span className="flex min-w-0 flex-1 items-center gap-2">
                        {open != null && (
                          <span
                            className={cn(
                              'h-2 w-2 shrink-0 rounded-full',
                              open ? 'bg-green-500' : 'bg-muted-foreground/40',
                            )}
                            title={open ? 'Market open' : 'Market closed'}
                            aria-label={open ? 'Market open' : 'Market closed'}
                          />
                        )}
                        <span className="min-w-0 flex-1 truncate text-sm" title={m.label ?? m.ticker}>
                          {m.label ?? m.ticker}
                        </span>
                      </span>
                      <span className="shrink-0 font-mono text-sm tabular-nums">
                        {formatPrice(m.price, classify(m) === 'FX')}
                      </span>
                      <span
                        className={cn(
                          'flex w-[4.5rem] shrink-0 items-center justify-end gap-0.5 font-mono text-xs tabular-nums',
                          m.day_change_pct == null
                            ? 'text-muted-foreground'
                            : dayUp
                              ? 'text-green-600'
                              : 'text-red-600',
                        )}
                        title="Day change"
                        aria-label={`Day change ${formatPct(m.day_change_pct)}`}
                      >
                        {m.day_change_pct != null &&
                          (dayUp ? (
                            <TrendingUp className="h-3 w-3" aria-hidden="true" />
                          ) : (
                            <TrendingDown className="h-3 w-3" aria-hidden="true" />
                          ))}
                        {formatPct(m.day_change_pct)}
                        <span className="text-muted-foreground/60">d</span>
                      </span>
                      <span
                        className={cn(
                          'flex w-[4.5rem] shrink-0 items-center justify-end gap-0.5 font-mono text-xs tabular-nums',
                          m.week_change_pct == null
                            ? 'text-muted-foreground'
                            : weekUp
                              ? 'text-green-600'
                              : 'text-red-600',
                        )}
                        title="7-day change"
                        aria-label={`7-day change ${formatPct(m.week_change_pct)}`}
                      >
                        {m.week_change_pct != null &&
                          (weekUp ? (
                            <TrendingUp className="h-3 w-3" aria-hidden="true" />
                          ) : (
                            <TrendingDown className="h-3 w-3" aria-hidden="true" />
                          ))}
                        {formatPct(m.week_change_pct)}
                        <span className="text-muted-foreground/60">7d</span>
                      </span>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))
        )}
      </div>
    </div>
  );
}
